import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ReplayCircleFilled as ReplayCircleFilledIcon,
} from '@mui/icons-material';

import ConfirmationDialog from '@components/ConfirmationDialog';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import {
  getCurrencyDetails,
  addCurrencyDetails,
  updateCurrencyDetails,
  deleteCurrencyDetails,
  invalidateCache,
  selectCurrencyDetailsList,
} from '../store/currency-details';

import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../components/TableToolbar';

import { viewCurrencyDetails } from '../payment-configuration.breadcrumbs';

function ConfigToolbar({
  setNewConfigDialogOpen,
  confirmRefreshHandler,
  permissions = {},
}) {
  return (
    <TableToolbar>
      <div>
        <Button
          variant="outlined"
          disabled={!permissions?.canRefreshCurrencyDetailsCache}
          onClick={() => confirmRefreshHandler()}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          Refresh Cache
        </Button>
        <Button
          disabled={!permissions?.canCreateCurrencyDetails}
          variant="contained"
          onClick={() => setNewConfigDialogOpen(true)}
          startIcon={<AddIcon />}>
          New
        </Button>
      </div>
    </TableToolbar>
  );
}

export default function CurrencyDetails({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currencyDetailsList = useSelector(selectCurrencyDetailsList);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
  const [showRefreshCacheConfirmation, setShowRefreshCacheConfirmation] =
    useState({ show: false });
  const [toDeleteConfig, setToDeleteConfig] = useState('');
  const [newConfigDialogOpen, setNewConfigDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    if (!permissions?.canViewCurrencyDetails) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewCurrencyDetails));
  }, [dispatch, navigate]);

  const deleteHandler = (id) => {
    setToDeleteConfig(id);
    setShowDeleteModal({ show: true });
  };

  const yesDeleteHandler = (id) => {
    dispatch(deleteCurrencyDetails({ id })).then((response) => {
      const { payload } = response;
      if (payload && payload.status === 'OK') {
        dispatch(showSuccessMessage('Config Deleted Successfully'));
      } else {
        dispatch(
          showErrorMessage({ message: payload.message, duration: 4000 }),
        );
      }
    });
  };

  const confirmRefreshHandler = () => {
    setShowRefreshCacheConfirmation({ show: true });
  };

  const yesConfirmRefreshHandler = () => {
    dispatch(invalidateCache()).then(({ payload }) => {
      if (payload && payload.status === 'OK') {
        dispatch(showSuccessMessage('Successfully Triggered Refresh'));
      } else {
        dispatch(
          showErrorMessage({ message: payload.message, duration: 4000 }),
        );
      }
    });
  };

  const resetForm = useCallback(() => {
    setNewConfigDialogOpen(false);
    setFormValues({});
    setIsEditMode(false);
  }, []);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getCurrencyDetails()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  const createNewConfig = (values) => {
    const { country, value } = values;
    const payload = isEditMode
      ? {
          country,
          value,
        }
      : values;

    return dispatch(
      isEditMode
        ? updateCurrencyDetails({
            payload,
            id: values.id,
          })
        : addCurrencyDetails({
            payload,
          }),
    ).then(({ payload: resp }) => {
      if (
        resp &&
        resp.data &&
        (resp.status === 'Created' || resp.status === 'OK')
      ) {
        dispatch(
          showSuccessMessage(
            `Config ${isEditMode ? 'Updated' : 'Added'} Successfully`,
          ),
        );
        return resetForm();
      }

      return { [FORM_ERROR]: resp.message };
    });
  };

  const validateCurrencyDetail = (values) => {
    const errors = {};

    if (!values.currency) {
      errors.currency = 'Currency is mandatory';
    }
    if (!values.currencyName) {
      errors.currencyName = 'Currency name is mandatory';
    }
    if (!values.minimum) {
      errors.minimum = 'Minimum Value is mandatory';
    }
    if (!values.currencyIso) {
      errors.currencyIso = 'Currency ISO is mandatory';
    }
    if (!values.exponent) {
      errors.exponent = 'Exponent is mandatory';
    }
    if (!values.country) {
      errors.country = 'Country is mandatory';
    }

    return errors;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="Currency Details"
      pageReady={!isFetchingData}>
      <DataGrid
        autoHeight
        density="compact"
        rows={currencyDetailsList}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        pageSize={10}
        rowsPerPageOptions={[5, 10, 25]}
        components={{
          Toolbar: () =>
            ConfigToolbar({
              setNewConfigDialogOpen,
              confirmRefreshHandler,
              permissions,
            }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => {
          setIsEditMode(true);
          setFormValues(row);
          setNewConfigDialogOpen(true);
        }}
        columns={[
          {
            field: 'id',
            headerName: 'ID',
            width: 100,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'currency',
            headerName: 'Currency',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
          },
          {
            field: 'currencyName',
            headerName: 'Currency',
            sortable: false,
            headerAlign: 'center',
            align: 'left',
            flex: 2,
          },
          {
            field: 'currencyIso',
            headerName: 'Currency ISO',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
          },
          {
            field: 'minimum',
            headerName: 'Minimum',
            sortable: false,
            headerAlign: 'right',
            align: 'right',
            flex: 1,
          },
          {
            field: 'exponent',
            headerName: 'Exponent',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
          },
          {
            field: 'country',
            headerName: 'Country',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
          },
          {
            headerName: 'Action',
            field: 'actions',
            type: 'actions',
            getActions: ({ row }) => [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                key="0"
                disabled={!permissions?.canDeleteCurrencyDetails}
                onClick={() => deleteHandler(row.id)}
              />,
            ],
          },
        ]}
      />
      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={yesDeleteHandler}
        data={toDeleteConfig}
        title="Delete Currency Detail"
        message="Are you sure you want to delete the selected currency?"
      />
      <ConfirmationDialog
        openDialog={showRefreshCacheConfirmation}
        yesHandler={yesConfirmRefreshHandler}
        title="Refresh Cache"
        message="Are you sure you want refresh the cache?"
      />
      <Dialog open={newConfigDialogOpen}>
        <DialogTitle>
          {isEditMode ? 'Update Existing Currency' : 'Add New Currency'}
        </DialogTitle>
        <Form
          onSubmit={createNewConfig}
          initialValues={isEditMode ? formValues : {}}
          validate={validateCurrencyDetail}
          render={({ submitError, handleSubmit, submitting }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label="Currency"
                    name="currency"
                    required
                    variant="filled"
                    fullWidth
                    InputProps={{
                      readOnly: !permissions?.canUpdateCurrencyDetails,
                    }}
                  />
                  <TextField
                    label="Currency Name"
                    name="currencyName"
                    required
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateCurrencyDetails,
                    }}
                  />
                  <TextField
                    label="Currency ISO"
                    name="currencyIso"
                    required
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateCurrencyDetails,
                    }}
                  />
                  <TextField
                    label="Minimum"
                    name="minimum"
                    required
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateCurrencyDetails,
                    }}
                  />
                  <TextField
                    label="Exponent"
                    name="exponent"
                    required
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateCurrencyDetails,
                    }}
                  />
                  <TextField
                    label="Country"
                    name="country"
                    required
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateCurrencyDetails,
                    }}
                  />
                </Box>
                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 20 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    !permissions?.canUpdateCurrencyDetails ||
                    !permissions?.canCreateCurrencyDetails ||
                    submitting
                  }>
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
